import * as L from 'leaflet';

interface ContourLayerOptions extends L.TileLayerOptions {
  transparent: boolean;
  labelColor?: string;
  labelFontSize?: string;
  contourColor?: string;
}

export class NMTContourLayer extends L.TileLayer {
  myoptions: ContourLayerOptions;

  constructor(urlTemplate: string, options: ContourLayerOptions) {
    super(urlTemplate, options);
    this.myoptions = {
      transparent: true,
      labelColor: 'red',
      labelFontSize: '12px',
      contourColor: 'rgba(0, 0, 0, 1)',
      ...options
    };
  }

  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const canvas = L.DomUtil.create('canvas', 'leaflet-tile') as HTMLCanvasElement;
    
    // Optymalizacja rozmiaru canvas: unikanie odwołań do window.mainmap
    const zoom = coords.z;
    let size = 256;
    if (zoom === 18) size = 512;
    else if (zoom === 19) size = 1024;
    else if (zoom >= 20) size = 2048;

    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const prom = this.fetchNMTFile(coords);
    const coords_right = { z: coords.z, x: coords.x + 1, y: coords.y } as L.Coords;
    const coords_down = { z: coords.z, x: coords.x, y: coords.y + 1 } as L.Coords;
    const prom_right = this.fetchNMTFile(coords_right);
    const prom_down = this.fetchNMTFile(coords_down);

    let data: Uint16Array | null = null;
    let data_right: Uint16Array | null = null;
    let data_down: Uint16Array | null = null;

    Promise.allSettled([prom, prom_right, prom_down]).then((results) => {
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          if (index === 0) data = result.value;
          if (index === 1) data_right = result.value;
          if (index === 2) data_down = result.value;
        }
        if (result.status === "rejected" && index === 0) {
          done(Error('Error'), canvas);
        }
      });
      if (data) {
        const t1 = performance.now();
        this.render(data, data_right, data_down, ctx, size, zoom);
        const t2 = performance.now();
        console.log(t2-t1 + ' ms');
        done(undefined, canvas);
      } else {
        done(Error('ERROR'), canvas);
      }
    });

    return canvas;
  }

  private async fetchNMTFile(coords: L.Coords) {
    const url = this.getTileUrl(coords);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error downloading NMT data');
    }
    const buffer = await response.arrayBuffer();
    return new Uint16Array(buffer); // 256x256
  }

  private scaleNearest(data: Uint16Array, origSize: number, size: number): Uint16Array {
    if (!data) return new Uint16Array(size * size);
    if (origSize === size) return data;
    
    const up = new Uint16Array(size * size);
    const ratio = size / origSize;
    
    for (let y = 0; y < size; y++) {
      const sy = Math.min(origSize - 1, (y / ratio) | 0);
      const srcRow = sy * origSize;
      const dstRow = y * size;
      for (let x = 0; x < size; x++) {
        const sx = Math.min(origSize - 1, (x / ratio) | 0);
        up[dstRow + x] = data[srcRow + sx] || 0;
      }
    }
    return up;
  }

private render(data: Uint16Array, data_right: Uint16Array | null, data_down: Uint16Array | null, ctx: CanvasRenderingContext2D, size: number, zoom: number) {
  let step = 1000;
  if (zoom === 18) step = 500;
  else if (zoom === 19) step = 200;
  else if (zoom >= 20) step = 100;

  // Obliczamy mnożnik skali w stosunku do bazowego kafelka 256x256
  const multiplier = size / 256;

  if (!this.myoptions.transparent) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
  }

  const origSize = Math.round(Math.sqrt(data.length));
  const srcData = origSize !== size ? this.scaleNearest(data, origSize, size) : data;
  const scaled_right = data_right ? this.scaleNearest(data_right, Math.round(Math.sqrt(data_right.length)), size) : null;
  const scaled_down = data_down ? this.scaleNearest(data_down, Math.round(Math.sqrt(data_down.length)), size) : null;

  const contourPoints = new Uint8Array(size * size);
  const contourValues = new Int32Array(size * size);

  // 1. Wykrywanie punktów poziomic
  for (let y = 0; y < size; y++) {
    const rowIdx = y * size;
    const nextRowIdx = (y + 1) * size;
    for (let x = 0; x < size; x++) {
      const idx = rowIdx + x;
      const h = srcData[idx];
      const hStep = (h / step) | 0;

      let isContour = false;
      let val = 0;

      let rightVal = (x < size - 1) ? srcData[idx + 1] : (scaled_right ? scaled_right[rowIdx] : null);
      if (rightVal !== null && hStep !== ((rightVal / step) | 0)) {
        isContour = true;
        val = Math.max(hStep, ((rightVal / step) | 0)) * step;
      }

      if (!isContour) {
        let bottomVal = (y < size - 1) ? srcData[nextRowIdx + x] : (scaled_down ? scaled_down[x] : null);
        if (bottomVal !== null && hStep !== ((bottomVal / step) | 0)) {
          isContour = true;
          val = Math.max(hStep, ((bottomVal / step) | 0)) * step;
        }
      }

      if (isContour) {
        contourPoints[idx] = 1;
        contourValues[idx] = val;
      }
    }
  }

  // 2. Rysowanie linii konturów bezpośrednio do ImageData
  const imgData = ctx.createImageData(size, size);
  
  const parseRgba = (s: string | undefined): [number, number, number, number] => {
    const def: [number, number, number, number] = [0, 0, 0, 255];
    if (!s || typeof s !== 'string') return def;
    const rgba = s.replace(/\s+/g, '');
    const m = rgba.match(/rgba?\((\d+),(\d+),(\d+)(?:,(0|1|0?\.\d+|\d+))?\)/i);
    if (m) {
      const r = Math.max(0, Math.min(255, parseInt(m[1], 10) || 0));
      const g = Math.max(0, Math.min(255, parseInt(m[2], 10) || 0));
      const b = Math.max(0, Math.min(255, parseInt(m[3], 10) || 0));
      let a = 255;
      if (m[4] !== undefined) {
        const aRaw = parseFloat(m[4]);
        if (aRaw <= 1) a = Math.round(aRaw * 255);
        else a = Math.round(Math.max(0, Math.min(255, aRaw)));
      }
      return [r, g, b, a];
    }
    return def;
  };

  const contourColorBytes = parseRgba(this.myoptions.contourColor);
  const totalPixels = size * size;
  for (let i = 0; i < totalPixels; i++) {
    const px = i * 4;
    if (contourPoints[i]) {
      imgData.data[px] = contourColorBytes[0];
      imgData.data[px + 1] = contourColorBytes[1];
      imgData.data[px + 2] = contourColorBytes[2];
      imgData.data[px + 3] = contourColorBytes[3];
    } else {
      imgData.data[px] = 255; imgData.data[px + 1] = 255; imgData.data[px + 2] = 255; imgData.data[px + 3] = 0;
    }
  }
  ctx.putImageData(imgData, 0, 0);

  // 3. Podpisywanie konturów
  const visited = new Uint8Array(size * size);
  
  const baseFontSize = parseInt(this.myoptions.labelFontSize || '12', 10);
  ctx.font = `bold ${baseFontSize * multiplier}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.strokeStyle = "white";
  ctx.lineWidth = 3 * multiplier;

  const labelThreshold = 400 * multiplier; 
  const padding = 12 * multiplier;

  const stack = new Int32Array(size * size);

  for (let y = 0; y < size; y++) {
    const rowIdx = y * size;
    for (let x = 0; x < size; x++) {
      const idx = rowIdx + x;

      if (contourPoints[idx] && !visited[idx]) {
        const seedX = x;
        const seedY = y;

        const currentHeight = contourValues[idx];
        const safeX = Math.max(padding, Math.min(size - padding, seedX));
        const safeY = Math.max(padding, Math.min(size - padding, seedY));
        
        ctx.strokeText(String(currentHeight / 100), safeX, safeY);
        ctx.fillStyle = this.myoptions.labelColor ?? 'red';
        ctx.fillText(String(currentHeight / 100), safeX, safeY);

        let stackPtr = 0;
        stack[stackPtr++] = idx;
        visited[idx] = 1;

        while (stackPtr > 0) {
          const cIdx = stack[--stackPtr];
          const cx = cIdx % size;
          const cy = (cIdx / size) | 0;

          for (let dy = -1; dy <= 1; dy++) {
            const ny = cy + dy;
            if (ny < 0 || ny >= size) continue;
            const nRowIdx = ny * size;

            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const nx = cx + dx;
              if (nx < 0 || nx >= size) continue;

              const nIdx = nRowIdx + nx;

              if (!visited[nIdx] && contourPoints[nIdx]) {
                const dist = Math.abs(nx - seedX) + Math.abs(ny - seedY);
                if (dist <= labelThreshold) {
                  visited[nIdx] = 1;
                  stack[stackPtr++] = nIdx;
                }
              }
            }
          }
        }
      }
    }
  }

  // 4. Nałożenie konturów na wierzch tekstów
  const overlay = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < totalPixels; i++) {
    if (contourPoints[i]) {
      const px = i * 4;
      overlay.data[px] = contourColorBytes[0];
      overlay.data[px + 1] = contourColorBytes[1];
      overlay.data[px + 2] = contourColorBytes[2];
      overlay.data[px + 3] = contourColorBytes[3];
    }
  }
  ctx.putImageData(overlay, 0, 0);
}


}
